import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import moment from "moment";
import ModalMobile from "../../../../components/shared/Modal/ModalMobile";
import Button from "../../../../components/shared/Button/Button";
import Divider from "../../../../components/shared/Divider/Divider";
import Input from "../../../../components/shared/Input/Input";
import { adminSearchService } from "../../../../services/adminSearch";
import { useInvalidate } from "../../../../hooks/useInvalidate";
import { useAdminSearchStore } from "../../store/adminSearchStore";
import { useAdminSearchSessions } from "../../hooks/useAdminSearchSessions";
import { toast } from "react-toastify";

const dateFields = ["sessionDate"];
const stringFields = ["sessionName"];

const mapSessionToForm = (session) => ({
  sessionName: session?.sessionName || "",
  sessionDate: session?.sessionDate
    ? moment(session.sessionDate).format("YYYY-MM-DD")
    : "",
});

const normalizePayload = (values) => {
  const payload = { ...values };

  dateFields.forEach((field) => {
    if (!(field in payload)) return;
    const raw = payload[field];
    if (!raw) {
      payload[field] = null;
      return;
    }
    const parsed = moment(raw, "YYYY-MM-DD", true);
    if (parsed.isValid()) {
      payload[field] = parsed.valueOf();
    } else {
      delete payload[field];
    }
  });

  stringFields.forEach((field) => {
    if (!(field in payload)) return;
    if (typeof payload[field] === "undefined") {
      delete payload[field];
    }
  });

  return payload;
};

export default function EditSessionModal({ isOpen, onClose }) {
  const invalidate = useInvalidate();
  const { sessionsState } = useAdminSearchStore();
  const { selectedSessionId } = sessionsState;
  const { data } = useAdminSearchSessions();
  const sessions = data?.sessions || [];
  const sessionSummary = useMemo(
    () => sessions.find((session) => session?.id === selectedSessionId),
    [sessions, selectedSessionId],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm({
    defaultValues: mapSessionToForm(sessionSummary),
  });

  useEffect(() => {
    if (sessionSummary) {
      reset(mapSessionToForm(sessionSummary));
    }
  }, [reset, sessionSummary]);

  const mutation = useMutation(async ({ id, data }) => {
    return adminSearchService.adminSearchUpdateSession({
      id,
      data,
    });
  });

  const onSubmit = useCallback(
    async (values) => {
      if (!selectedSessionId) return;
      const dirtyKeys = Object.keys(dirtyFields || {});
      const dirtyValues = dirtyKeys.reduce((acc, key) => {
        acc[key] = values[key];
        return acc;
      }, {});
      const normalized = normalizePayload(dirtyValues);

      if (!Object.keys(normalized).length) {
        toast.info("No changes to save.");
        return;
      }

      try {
        await mutation.mutateAsync({
          id: selectedSessionId,
          data: normalized,
        });
        toast.success("Changes saved.");
        invalidate(["adminSearchSessions_key"]);
        onClose?.();
      } catch (err) {
        console.error("Failed to update session", err);
        toast.error(err?.message || "Failed to update session");
      }
    },
    [dirtyFields, invalidate, mutation, onClose, selectedSessionId],
  );

  const isSaving = mutation.isPending;

  if (!isOpen || !selectedSessionId) return null;

  return (
    <ModalMobile
      className="font-manrope w-full max-w-2xl pt-10"
      isOpen={isOpen}
      onClose={onClose}
      showClose={true}
    >
      <div className="px-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-2xl font-semibold text-gray900">
              Edit session
            </div>
            <div className="text-base text-gray600">
              {sessionSummary?.sessionName || selectedSessionId}
            </div>
          </div>
        </div>

        <Divider className="my-4" color="gray200" type="horizontal" />

        <form className="flex flex-col gap-5">
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              control={control}
              name="sessionName"
              label="Session name"
              rules={{ required: false }}
              isDisabled={isSaving}
              size="md"
            />
            <Input
              control={control}
              name="sessionDate"
              label="Session date"
              type="date"
              rules={{ required: false }}
              isDisabled={isSaving}
              size="md"
            />
          </div>
        </form>
      </div>

      <Divider className="mt-6" color="gray200" type="horizontal" />

      <div className="flex items-center justify-between gap-3 px-6 py-4">
        <div className="text-base text-gray600">
          {isSaving && "Saving changes…"}
          {!isSaving && mutation.error
            ? mutation.error?.message || "Failed to update session"
            : null}
        </div>
        <div className="flex gap-2">
          <Button
            color="gray100"
            onClick={onClose}
            size="md"
            isDisabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            isLoading={isSaving}
            size="md"
          >
            Save changes
          </Button>
        </div>
      </div>
    </ModalMobile>
  );
}
